import { v4 } from "uuid";

import type { ModificationSource } from "@intelligems/ig-types/dtos/modification-source";

import type {
  EmbeddedMod,
  FromAiFormatContext,
} from "@src/store/slices/experienceBuilder/types";

/**
 * Frontend-only meta fields restored from the AI's payload + the call
 * context. Returned as a plain object so handlers can spread it into their
 * own result and stay in control of mod-specific resolution.
 */
export type SharedAiMeta = {
  id: string;
  variationId: string;
  source: ModificationSource | undefined;
  createdAtUrl: string | undefined;
};

/**
 * Shared frontend-only meta resolution for `ModHandler.fromAiFormat`.
 *
 * - `id`: AI's payload wins (updates an existing mod by id); else the
 *   existing mod's id (when context.existing is present); else a freshly
 *   generated uuid (a brand-new mod).
 * - `variationId`: always the call-scoped `context.variationId`. The AI's
 *   per-call top-level field is authoritative. Existing-mod lookup is
 *   already scoped to the same variation in `aiModsToFullFormat`, so there's
 *   no cross-variation conflict to resolve here.
 * - `source`: preserved from `existing` if updating, else `"ai"` for new.
 * - `createdAtUrl`: preserved from `existing` if updating, else the page
 *   URL the AI is editing on right now.
 *
 * Handlers typically use this as:
 *
 * ```ts
 * fromAiFormat: (aiMod, context) => ({
 *   ...(context.existing ?? handler.defaults()),
 *   ...aiMod,
 *   ...sharedFromAiFormat(aiMod, context),
 * }),
 * ```
 *
 * (Spread order matters: defaults/existing first, AI's payload over them,
 * shared meta last so it wins for id/variationId/source/createdAtUrl.)
 */
export function sharedFromAiFormat<T extends EmbeddedMod>(
  aiMod: { id?: string },
  context: FromAiFormatContext<T>
): SharedAiMeta {
  const { existing, createdAtUrl, variationId } = context;
  return {
    id: aiMod.id ?? existing?.id ?? v4(),
    variationId,
    source: existing ? existing.source : "ai",
    createdAtUrl: existing?.createdAtUrl ?? createdAtUrl,
  };
}
